import { Navigate, useNavigate, useParams } from 'react-router';
import styled, { css } from 'styled-components';

import { ScreenLoader } from '@dutchie-pos/components';

import { headerHeight } from 'src/app/components/header';
import { BypassDelivery } from 'src/app/components/checkout/bypass-delivery';
import { CartHeader } from 'src/app/components/checkout/cart-header';
import { CheckoutSuccess } from 'src/app/components/checkout/checkout-success';
import { PaymentFormUpdated } from 'src/app/components/checkout/payment-form-updated';
import { useLocation } from 'src/queries/curbside/location-details';
import { useActiveCustomer } from 'src/queries/curbside/use-carts';
import { useGoodCannaStatus } from 'src/utils/hooks/use-good-canna-status';

import { type CartParams } from '../cart';
import { usePayment } from './use-payment';

export const Payment = (): JSX.Element => {
  const { totals, showCheckoutSuccess, handlePaymentSuccess, handlePaymentFailure } = usePayment();
  const navigate = useNavigate();
  const { customerId, shipmentId } = useParams<CartParams>();
  const { data: location } = useLocation();
  const showBypassStateSystem = location?.Features['ShowStateBypass'] ?? false;
  const { data: activeCustomer, isLoading: isActiveCustomerLoading } = useActiveCustomer();
  const { showMedCertificationBanner } = useGoodCannaStatus(activeCustomer?.GoodCannaStatus);

  if (showCheckoutSuccess) {
    return <CheckoutSuccess {...totals} />;
  }

  if (isActiveCustomerLoading) {
    return <ScreenLoader speed='fast' top={headerHeight} visible />;
  }

  if (showMedCertificationBanner && shipmentId && customerId) {
    return <Navigate replace to={`/cart/${shipmentId}/${customerId}`} />;
  }

  return (
    <Container>
      <CartHeader onGoBack={() => navigate(`/checks/${shipmentId}/${customerId}`)} />
      {showBypassStateSystem && (
        <div data-testid="bypass-state-section">
          <SectionLabel>Bypass State System</SectionLabel>
          <BypassDelivery />
        </div>
      )}
      <SectionLabel>Payment</SectionLabel>
      <PaymentFormUpdated totals={totals} onFailure={handlePaymentFailure} onPaymentSuccess={handlePaymentSuccess} />
    </Container>
  );
};

// overflow-y: scroll;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 24px;
`;

const LabelCss = css`
  color: black;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 2rem;
`;

const SectionLabel = styled.div`
  ${LabelCss}
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;
